import { Pages } from 'modules/navigation/domain/enums/pages';
import { useOrganizer } from 'modules/organizer/ui/hooks/use-organizer';
import { getAccessOptions, getVisibilityOptions } from 'modules/template-editor/domain/consts/form/template/options';
import { templatePresets, TemplatePresetsNameList } from 'modules/template-editor/domain/consts/form/template/template-presets';
import { Values } from 'modules/template-editor/domain/interface/form/template';
import TemplateForm from 'modules/template-editor/ui/components/elements/forms/template-form';
import { useLingui } from '@lingui/react/macro';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useIsAdmin } from 'shared/hooks/use-is-admin';
import { history } from 'store/store';
import { useDispatchWithToast } from 'store/use-dispatch-with-toast';
import { UDText } from 'ud-ui/components';
import Modal from 'ud-ui/components/modal';
import { addLike, copyTemplate } from 'modules/template-editor/store/templates/actions';
import { selectActiveTemplate } from 'modules/template-editor/store/templates/selectors';
import { resetPressedState } from 'modules/template-editor/store/templates/templates';
import useTemplates from 'modules/template-editor/ui/hooks/use-templates';

const PRESET: TemplatePresetsNameList = 'copy';
// eslint-disable-next-line lingui/no-unlocalized-strings -- имя копии уходит в базу, это данные, а не подпись интерфейса
const POSTFIX = ' (копия)';

function useCopyModal(templateId: number) {
  const { t } = useLingui();
  const dispatch = useDispatch();
  const dispatchWithToast = useDispatchWithToast();
  const { info } = useOrganizer();
  const activeTemplate = useSelector(selectActiveTemplate);
  const { templates, isProcessed, isFinished, currentProcess } = useTemplates();
  const isAdmin = useIsAdmin();

  const [isOpen, setIsOpen] = useState(false);
  const open = useCallback(() => {
    setIsOpen(true);
  }, []);
  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const onSubmit = useCallback(
    (values: Values) => {
      if (!info) {
        console.warn({ event: 'failed to copy template: info is undefined', info });
        return;
      }

      const { width, height, name } = values;
      const resolution = { width: +width, height: +height };
      dispatchWithToast(
        copyTemplate({
          ownerId: info.id,
          templateId,
          name: name.toString(),
          resolution,
          process: 'copy template',
        }),
        { error: t`Не удалось создать копию` },
      );
    },
    [dispatchWithToast, info, t, templateId],
  );

  const initialValue = {
    width: activeTemplate?.resolution.width?.toString() ?? '1920',
    height: activeTemplate?.resolution.height?.toString() ?? '1080',
    name: activeTemplate?.name ? activeTemplate?.name + POSTFIX : '',
    visibility: getVisibilityOptions().find((option) => option.value === activeTemplate?.visibility) ?? getVisibilityOptions()[0],
    access: getAccessOptions().find((option) => option.value === activeTemplate?.access) ?? getAccessOptions()[0],
  };

  useEffect(() => {
    if (!info) {
      return;
    }
    if (!isFinished) {
      return;
    }
    if (currentProcess !== 'copy template') {
      return;
    }
    close();
    dispatch(resetPressedState());
    dispatchWithToast(addLike({ templateId: templateId, userId: info.id }), { error: t`Не удалось поставить лайк` });
    history.push(Pages.TEMPLATE.INDEX, { isCoppedTemplate: true });
  }, [isProcessed, isFinished, close, dispatch, dispatchWithToast, info, templateId, currentProcess, t]);

  const existingNames = useMemo(() => templates.map((template) => template.name.trim()), [templates]);

  return {
    modal: (
      <Modal show={isOpen} onClose={close} size="md">
        <UDText type={'title-3'} className="mb-5">
          {t(templatePresets[PRESET].header)}
        </UDText>
        <TemplateForm
          preset={PRESET}
          initialValues={initialValue}
          onSubmit={onSubmit}
          existingNames={existingNames}
          advancedAccess={isAdmin}
          isLoading={isProcessed}
        />
      </Modal>
    ),
    close,
    open,
  };
}

export default useCopyModal;
